import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Building2, ChevronRight, Link2, Network, PlusCircle, Users, Users2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useEntitlements } from '@/stores/entitlements';

interface Item {
  to: string;
  label: string;
  description: string;
  icon: typeof PlusCircle;
  iconBg: string;
  iconColor: string;
  /** Papéis que enxergam o item. Ausente = todos. */
  roles?: string[];
}

// Papéis que fazem CADASTROS/VÍNCULOS. Cliente final (cliente/proprietario/operador/gestor)
// fica de fora: não cadastra usuário/dispositivo nem vincula (só notificações e geocercas).
const CADASTRO_ROLES = ['admin', 'revenda', 'operador_suporte', 'tecnico'];

const items: Item[] = [
  {
    to: '/settings/devices/new',
    label: 'Adicionar Embarcações',
    description: 'Cadastre novas embarcações (nome/registro, modelo, ID único, foto)',
    icon: PlusCircle,
    iconBg: 'bg-status-running/10',
    iconColor: 'text-status-running',
    roles: CADASTRO_ROLES,
  },
  {
    to: '/users',
    label: 'Usuários',
    description: 'Gerenciar usuários do sistema, papéis, funções e operadores',
    icon: Users,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    roles: CADASTRO_ROLES,
  },
  {
    to: '/revenda/clientes',
    label: 'Clientes da Plataforma',
    description: 'Cadastre clientes que vão acessar a plataforma (login), libere módulos e adicione embarcações',
    icon: Building2,
    iconBg: 'bg-brand/10',
    iconColor: 'text-brand',
    roles: ['admin', 'revenda'],
  },
  {
    to: '/admin/tenants',
    label: 'Revendas',
    description: 'Liste e administre revendas — módulos/cotas, white-label, planos e gateway',
    icon: Network,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    roles: ['admin'],
  },
  {
    to: '/settings/clients',
    label: 'Clientes (grupos)',
    description: 'Cadastro de clientes e vínculo com embarcações',
    icon: Users2,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    roles: CADASTRO_ROLES,
  },
  {
    to: '/settings/notifications',
    label: 'Notificações',
    description: 'Canais de alerta por evento: web sonoro, e-mail, SMS e Firebase (push)',
    icon: Bell,
    iconBg: 'bg-status-blocked/10',
    iconColor: 'text-status-blocked',
  },
  {
    to: '/settings/links',
    label: 'Vinculações',
    description: 'Vincular embarcações, clientes, notificações e geocercas a usuários',
    icon: Link2,
    iconBg: 'bg-status-ready/10',
    iconColor: 'text-status-ready',
    roles: CADASTRO_ROLES,
  },
];

export function SettingsPage() {
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  const visible = useMemo(
    () => items.filter((it) => !it.roles || (!!papel && it.roles.includes(papel))),
    [papel],
  );
  return (
    <div className="mx-auto max-w-2xl space-y-3 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-bold">Configurações</h1>
        <p className="text-sm text-muted-foreground">
          Cadastros e gerenciamento do ecossistema NautMax.
        </p>
      </div>
      <Card>
        <CardContent className="p-0">
          <ul className="divide-y divide-border">
            {visible.map((it) => (
              <li key={it.to}>
                <Link to={it.to} className="flex w-full items-center gap-3 px-4 py-3 hover:bg-accent">
                  <span
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                      it.iconBg,
                      it.iconColor,
                    )}
                  >
                    <it.icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{it.label}</p>
                    <p className="truncate text-xs text-muted-foreground">{it.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
